import React, {ChangeEvent, useMemo, useState} from 'react';
import {polygonsBorderingOptions, polygonsShowOptions} from "../../helpers/StyleConstants";

import JawgAPIs from "../../APIs/JawgAPIs";
import TownAPIs from "../../APIs/TownAPIs";

import {Location, LocationType} from "../../types/Location";

import IAgence from "../../interfaces/IAgence";
import ITown from "../../interfaces/ITown";
import IMapConfig from "../../interfaces/IMapConfig";
import IPolygon from "../../interfaces/IPolygon";
import IPartner from "../../interfaces/IPartner";

import LocationSearchBar from "../searchbar/LocationSearchBar";
import AgencySearchBar from "../searchbar/AgencySearchBar";
import {fetchPartners, fetchPartnersLocation, fetchTownsFromLocation, fetchTownsInRadius} from "./fetch";
import TownHelper from "../../helpers/Town";
import SearchButton from "./SearchButton";
import Notifier from "../../helpers/Notifier";
import {ERROR, MISCONFIGURED_CATCHMENT_AREA} from "../../helpers/Message";

interface AddressFormProps {
    mapConfig: IMapConfig,
    partners: IPartner[],
    setMapConfig: Function,
    setPartners: Function,
    setMapLoader: Function
}

function AddressForm(props: AddressFormProps) {
    const [location, setLocation] = useState<Location | null>(null);
    const [address, setAddress] = useState<string>('');
    const [agency, setAgency] = useState<IAgence | null>(null);

    const [searchType, setSearchType] = useState<string>("location");

    const [buttonDisabled, disableButton] = useState<boolean>(true);

    const handleSearchTypeSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
        disableButton(true);
        setSearchType(e.target.value);
        setLocation(null);
        setAgency(null);
    }

    const handleSearchButtonClick = async () => {
        if (searchType === 'location') {
            await handleLocationSearch();
            return;
        }

        await handleAgencySearch()
    }

    const handleAgencySearch = async () => {
        if (agency) {
            props.setMapLoader(true);

            try {
                const result : ITown[] = await TownAPIs.findCatchmentAreaByAgency({ idAgence: agency.id });

                if (result) {
                    const towns = result.map((town: ITown) => TownHelper.invertLatLng(town));

                    const position = { lat: agency.latitude, lng: agency.longitude };
                    props.setMapConfig((config : IMapConfig) => ({...config, position, canISetView: true }));

                    // Get polygons for towns and towns in radius
                    const polygons : IPolygon[] = getPolygons(towns, [...props.mapConfig.polygons], true);
                    props.setMapConfig((config : IMapConfig) => ({...config, polygons }));

                    // Partners treatment
                    await fetchAndUpdatePartners(towns);
                } else {
                    Notifier.createNotification(ERROR, MISCONFIGURED_CATCHMENT_AREA, "danger", 10000);
                }

                props.setMapLoader(false);
            } catch (error) {
                console.log(error);
                props.setMapLoader(false);
            }
        }
    }

    const handleLocationSearch = async () => {
        if (location) {
            props.setMapLoader(true);

            try {
                const result = await fetchTownsFromLocation(location);

                if (result) {
                    const towns = result.map((town: ITown) => TownHelper.invertLatLng(town));

                    // Set position
                    const position = await getMapPosition(towns, address, location.type);
                    props.setMapConfig((config : IMapConfig) => ({ ...config, position, canISetView: true }));

                    // Get polygons for towns and towns in radius
                    let polygons : IPolygon[] = getPolygons(towns, [...props.mapConfig.polygons], true);

                    const fetchedTownsInRadius = (
                        await fetchTownsInRadius(towns, location.type)
                    ).map(town => TownHelper.invertLatLng(town));

                    polygons = getPolygons(fetchedTownsInRadius, polygons, false);
                    props.setMapConfig((config : IMapConfig) => ({ ...config, polygons }));

                    // Partners treatment
                    await fetchAndUpdatePartners(towns);
                }

                props.setMapLoader(false);
            } catch (error) {
                console.log(error);
                props.setMapLoader(false);
            }
        }
    }

    const fetchAndUpdatePartners = async (towns: ITown[]) => {
        const fetchedPartners: any[] = await fetchPartners(towns);
        const newPartners = getNewPartners(fetchedPartners, towns);

        const { partnersLocations  } = props.mapConfig;
        const fetchedPartnersLocation : any[] = await fetchPartnersLocation(towns);

        fetchedPartnersLocation.forEach(p => partnersLocations?.set([p.ville, p.code].join(','), p.agences));

        props.setMapConfig((config : IMapConfig) => ({ ...config, partnersLocations }));
        props.setPartners((partners : IPartner[]) => [...partners, ...newPartners]);
    }

    const getNewPartners = (partners: any[], towns: ITown[]) : IPartner[] => {
        const newPartners: IPartner[] = [];

        partners.forEach((agency: any) => {
            const town = towns.find(t => t.normalizedName === agency.ville && t.postalCode === agency.code);

            if (town) {
                if (town.townId && town.insee && town.name) {
                    newPartners.push({
                        townId: town.townId,
                        insee: town.insee,
                        name: town.name,
                        postalCode: agency.code,
                        nbAgencies: Number(agency.nbagences),
                    });
                }
            }
        });

        return newPartners.filter(p1 => !props.partners.some(p2 => p1.townId === p2.townId && p1.insee === p2.insee && p1.postalCode === p2.postalCode));
    }

    const getMapPosition = async (towns: ITown[], addressSearch: string, locationType: LocationType) : Promise<any> => {
        if(locationType === LocationType.City) {
            const town = towns[0];

            if(addressSearch && town.name) {
                const position : any = await JawgAPIs.getJawgPosition({name: town.name}, addressSearch);
                return { lat: position[0], lng: position[1] };
            }

            return { lat: town.lat, lng: town.lng };
        }

        return TownHelper.calculateTownsCenterPosition(towns);
    }

    const getPolygons = (towns: ITown[], polygons: IPolygon[], select: boolean) : IPolygon[] => {
        const updPolygons = [...polygons];

        towns.forEach(t => {
            if(t.townId && t.insee && t.lat && t.lng && t.name && t.normalizedName && t.postalCode && t.postalCodes && t.geoJson) {
                const index = updPolygons.findIndex(p => t.insee === p.insee);
                const polygonExists = index >= 0;

                if (!polygonExists) {
                    updPolygons.push({
                        townId: t.townId,
                        insee: t.insee,
                        lat: t.lat,
                        lng: t.lng,
                        name: t.name,
                        normalizedName: t.normalizedName,
                        postalCodes: t.postalCodes,
                        positions: t.geoJson.coordinates,
                        options: select ? polygonsShowOptions : polygonsBorderingOptions,
                        isSelected: select,
                    });
                }

                if (polygonExists && select) {
                    updPolygons[index] = {...updPolygons[index], options: polygonsShowOptions, isSelected: true}
                }
            }
        })

        return updPolygons;
    }

    const Search = useMemo(() => () => {
        if (searchType === "location") {
            return (
                <>
                    <LocationSearchBar disabled={false} disableSearchButton={disableButton} setLocation={setLocation} />
                    <div className="formContent bxd3 bxm12">
                        <div className="inputDefaultContent">
                            <input className="inputContent"
                                   type="text"
                                   name="address"
                                   placeholder="Adresse"
                                   onChange={(e:any) => setAddress(e.target.value)}
                                   value={address}
                                   disabled={true}
                            />
                        </div>
                    </div>
                </>
            )
        }

        return <AgencySearchBar disabled={false} disableSearchButton={disableButton} setAgency={setAgency} />
    }, [searchType, address]);

    return (
        <div className="formContainer">
            <div className="formStructure bxrow">
                <div className="formContent bxm12">
                    <div className="selectDefaultContent">
                        <select value={searchType} className="selectContent" onChange={handleSearchTypeSelectChange}>
                            <option value="location">Localisation</option>
                            <option value="agency">Agence</option>
                        </select>
                    </div>
                </div>
                <Search />
                <SearchButton disabled={buttonDisabled} onClick={handleSearchButtonClick} />
            </div>
        </div>
    );
}

export default AddressForm;
